import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { apiClient } from '@copro/api';
import { enqueueSnackbar } from '../../../../../components/GlobalSnackbar/snackbarStore';
import { isSupabaseError } from '../../../../../constants/supabaseErrors';
import { useSpace } from '../../../../../hooks/useSpace';
import type { DisplayedSubjectQuote } from '../../../../../types';
import { keys as documentKeys } from '../../../../documents/api/keys';
import { useDeleteDocuments } from '../../../../documents/api/useDeleteDocuments';
import useDeleteFilesFromGoogleDrive from '../../../../documents/api/useDeleteFilesFromGoogleDrive';
import { invalidatePendingQuoteViews } from '../../../api/invalidatePendingQuoteViews';
import { keys } from '../../../api/keys';

type Props = {
  quote: DisplayedSubjectQuote;
  subjectId: string;
  onClose: () => void;
};

export default function useDeleteQuoteModal(props: Props) {
  const { currentSpace } = useSpace();
  const queryClient = useQueryClient();
  const { mutateAsync: deleteDocuments, isPending: isDeletePending } = useDeleteDocuments();
  const { mutateAsync: deleteFilesFromDrive, isPending: isDrivePending } = useDeleteFilesFromGoogleDrive();

  const [isLocalPending, setIsLocalPending] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const providerName = props.quote.providers?.name || props.quote.provider_name || 'ce devis';

  const documentNames = useMemo(() => {
    const names: string[] = [];
    const urls = new Set<string>();

    for (const doc of props.quote.documents ?? []) {
      if (doc.name) {
        names.push(doc.name);
      }
      if (doc.file_url) {
        urls.add(doc.file_url);
      }
    }

    if (
      props.quote.approved_document_url &&
      !urls.has(props.quote.approved_document_url)
    ) {
      names.push('Bon pour accord');
    }

    return names;
  }, [props.quote.documents, props.quote.approved_document_url]);

  const documentsCount = documentNames.length;
  const hasDocuments = documentsCount > 0;

  async function handleDeleteQuoteOnly() {
    setIsLocalPending(true);
    setServerError(null);

    try {
      if (hasDocuments) {
        // Unlink documents before deletion so that database cascade does not remove them
        await apiClient
          .from('documents')
          .update({ subject_action_id: null, quote_id: null })
          .eq('subject_action_id', props.quote.id);

        await apiClient
          .from('documents')
          .update({ quote_id: null })
          .eq('quote_id', props.quote.id);
      }

      // Delete from subject_actions if it exists there (cascades trigger to subject_quotes)
      await apiClient
        .from('subject_actions')
        .delete()
        .eq('id', props.quote.id);

      // Also ensure it is deleted directly from subject_quotes
      await apiClient
        .from('subject_quotes')
        .delete()
        .eq('id', props.quote.id);

      queryClient.invalidateQueries({ queryKey: keys.subTopicQuotes(props.subjectId) });
      queryClient.invalidateQueries({ queryKey: keys.subTopic(props.subjectId) });
      queryClient.invalidateQueries({ queryKey: documentKeys.getDocumentsBySubTopicId(props.subjectId) });
      queryClient.invalidateQueries({ queryKey: [...keys.all, 'activityTimeline'] });
      if (currentSpace?.id) {
        invalidatePendingQuoteViews(queryClient, currentSpace.id);
      }

      enqueueSnackbar('Devis supprimé.', 'success');
      props.onClose();
    } catch (error: unknown) {
      if (isSupabaseError(error)) {
        setServerError(error.message);
        return;
      }
      setServerError('Une erreur est survenue lors de la suppression.');
    } finally {
      setIsLocalPending(false);
    }
  }

  async function handleDeleteWithDocuments() {
    setIsLocalPending(true);
    setServerError(null);

    try {
      const { data: dbDocs } = await apiClient
        .from('documents')
        .select('id, file_url')
        .or(`quote_id.eq.${props.quote.id},subject_action_id.eq.${props.quote.id}`);

      const allDocIds = Array.from(
        new Set([
          ...(props.quote.documents ?? []).map((doc) => doc.id),
          ...(dbDocs ?? []).map((doc) => doc.id),
        ].filter((id): id is string => Boolean(id)))
      );

      const allFileUrls = Array.from(
        new Set([
          ...(props.quote.documents ?? []).map((doc) => doc.file_url),
          ...(dbDocs ?? []).map((doc) => doc.file_url),
          props.quote.approved_document_url,
        ].filter((url): url is string => Boolean(url)))
      );

      if (currentSpace && allFileUrls.length > 0) {
        try {
          await deleteFilesFromDrive({
            fileUrls: allFileUrls,
            spaceId: currentSpace.id,
          });
        } catch (err) {
          console.warn('Could not delete file from Google Drive:', err);
        }
      }

      if (allDocIds.length > 0) {
        await deleteDocuments({ ids: allDocIds });
      }

      await apiClient
        .from('documents')
        .delete()
        .or(`quote_id.eq.${props.quote.id},subject_action_id.eq.${props.quote.id}`);

      await apiClient
        .from('subject_actions')
        .delete()
        .eq('id', props.quote.id);

      await apiClient
        .from('subject_quotes')
        .delete()
        .eq('id', props.quote.id);

      queryClient.invalidateQueries({ queryKey: keys.subTopicQuotes(props.subjectId) });
      queryClient.invalidateQueries({ queryKey: keys.subTopic(props.subjectId) });
      queryClient.invalidateQueries({ queryKey: documentKeys.getDocumentsBySubTopicId(props.subjectId) });
      queryClient.invalidateQueries({ queryKey: [...keys.all, 'activityTimeline'] });
      if (currentSpace?.id) {
        invalidatePendingQuoteViews(queryClient, currentSpace.id);
      }

      enqueueSnackbar(
        documentsCount > 1 ? 'Devis et documents supprimés.' : 'Devis et document supprimé.',
        'success'
      );
      props.onClose();
    } catch (error: unknown) {
      if (isSupabaseError(error)) {
        setServerError(error.message);
        return;
      }
      setServerError('Une erreur est survenue lors de la suppression.');
    } finally {
      setIsLocalPending(false);
    }
  }

  const isPending = isLocalPending || isDeletePending || isDrivePending;

  function handleClose() {
    if (!isPending) {
      props.onClose();
    }
  }

  return {
    providerName,
    handleClose,
    documentNames,
    documentsCount,
    hasDocuments,
    isPending,
    serverError,
    handleDeleteQuoteOnly,
    handleDeleteWithDocuments,
  };
}
